import React, { useState } from "react";
import { Box, Typography, Chip, alpha, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import {
  RocketLaunchRounded,
  GroupRounded,
  AutoAwesomeRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const SidebarAICard = ({ openSide }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  if (!openSide) {
    // Expanded sidebar view
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box
          sx={{
            width: "100%",
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => navigate("/dashboard/ai")}
            sx={{
              width: "100%",
              height: "100%",
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              borderRadius: "0.75rem",
              padding: "2px",
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1,
              )}, ${alpha(theme.palette.secondary.main, 0.1)})`,
              overflow: "hidden", // Add this to contain the pseudo-element

              // Animated border container - centered
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "200%", // Make it larger than the container
                height: "5rem", // Make it larger than the container
                background: `linear-gradient(
        0deg,
        ${theme.palette.primary.main},
        ${theme.palette.secondary.main}
      )`,
                animation: "spin 3s linear infinite",
                zIndex: 0, // Ensure it's behind the content
              },

              "@keyframes spin": {
                "0%": {
                  transform: "translate(-50%, -50%) rotate(0deg)",
                },
                "100%": {
                  transform: "translate(-50%, -50%) rotate(360deg)",
                },
              },
            }}
          >
            {/* Inner content box */}
            <Box
              sx={{
                width: "100%",
                borderRadius: "calc(0.75rem - 3px)",
                background: theme.palette.background.paper,
                p: 2,
                position: "relative",
                zIndex: 1,
                transition: "all 0.3s ease",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <motion.div
                    animate={{ rotate: isHovered ? 360 : 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      }}
                    >
                      <AutoAwesomeRounded sx={{ fontSize: 20 }} />
                    </Box>
                  </motion.div>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: "0.875rem",
                      }}
                    >
                      AI Managers
                    </Typography>
                    <Box sx={{ display: "flex", gap: "0.5rem" }}>
                      <Chip
                        label="New"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                      />
                      <Chip
                        label="Coming Soon"
                        size="small"
                        disabled
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Stats */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <GroupRounded
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    8 Specialists
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </motion.div>
    );
  }

  // Collapsed sidebar view
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => navigate("/dashboard/ai")}
        sx={{
          width: "100%",
          mb: 2,
          position: "relative",
          cursor: "pointer",
          borderRadius: "0.75rem",
          padding: "3px",

          // Animated gradient border using pseudo-element
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "0.75rem",
            padding: "2px",
            background: `conic-gradient(
              from 0deg,
              ${theme.palette.primary.main},
              ${theme.palette.secondary.main},
              ${theme.palette.primary.main}
            )`,
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          },

          "@keyframes spin": {
            "0%": {
              transform: "rotate(0deg)",
            },
            "100%": {
              transform: "rotate(360deg)",
            },
          },
        }}
      >
        {/* Inner content box */}
        <Box
          sx={{
            width: "100%",
            borderRadius: "calc(0.75rem - 3px)",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.95,
            )}, ${alpha(theme.palette.background.paper, 0.97)})`,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            position: "relative",
            zIndex: 1,
            minHeight: "80px",
          }}
        >
          <motion.div
            animate={{ rotate: isHovered ? 360 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <RocketLaunchRounded sx={{ fontSize: 18 }} />
            </Box>
          </motion.div>

          <Typography
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              color: theme.palette.primary.main,
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            AI Team
          </Typography>

          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              fontWeight: 400,
              color: theme.palette.text.secondary,
              textAlign: "center",
            }}
          >
            Coming Soon
          </Typography>

          <AutoAwesomeRounded
            sx={{
              fontSize: 14,
              color: theme.palette.primary.main,
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 0.6 },
                "50%": { opacity: 1 },
              },
            }}
          />
        </Box>
      </Box>
    </motion.div>
  );
};

export default SidebarAICard;
